'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { isAwaitingMerchantApproval } from '@/lib/pickup-approval';
import Link from 'next/link';
import {
 CheckCircle2,
 ChefHat,
 ChevronDown,
 Clock,
 CreditCard,
 ExternalLink,
 Loader2,
 Package,
 Receipt,
 Trash2,
 X,
} from 'lucide-react';

import { ContentSkeleton } from '@/components/ui/content-skeleton';
import { PickupClaimCode } from '@/components/menu/PickupClaimCode';
import { Button } from '@/components/ui/button';
import { TableSessionBill } from '@/components/menu/TableSessionBill';
import { toHslTriplet } from '@/lib/menu-themes/hsl';
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet';
import { cn } from '@/components/ui/utils';
import type { TemplateSurfacePalette } from '@/app/dashboard/templates/components/lib/templateExperience';
import { useCustomerOrderStatus } from '@/hooks/customer/useCustomerOrderStatus';
// Re-exported below: it moved to hooks/ so the place chrome can count a guest's
// orders without importing this whole sheet.
import { useGuestOrderRecords } from '@/hooks/customer/useGuestOrderRecords';
import { customerFetch } from '@/lib/customer-bridge';
import { getGuestUsername } from '@/lib/customer/guest-identity';
import { formatMoney as money } from '@/lib/money';
import {
 isUnpaidPaymentStatus,
 removeGuestOrder,
 type GuestOrderRecord,
} from '@/lib/customer/guest-orders';
import { legacyOrderTrackPath } from '@/lib/routing/place-href';

const STEP_INDEX: Record<string, number> = {
  placed: 0, pending: 0, accepted: 0, awaiting_prepayment: 0, preparing: 1, ready: 2, served: 3, awaiting_payment: 3, paid: 3, completed: 3,
};

type CustomerOrderApiRecord = {
 serviceOrderId: string;
 orderNumber?: string;
 ticketCode?: string | null;
 businessName?: string | null;
 menuSlug?: string | null;
 businessId?: string | null;
 branchId?: string | null;
 status?: string | null;
 paymentStatus?: string | null;
 total?: number;
 currency?: string | null;
 items?: Array<{ title?: string; quantity?: number }>;
 createdAt?: string;
 accessToken?: string;
};

function projectCustomerOrder(order: CustomerOrderApiRecord): GuestOrderRecord {
 return {
  id: order.serviceOrderId,
  token: order.accessToken || '',
  orderNumber: order.orderNumber || '',
  ticketCode: order.ticketCode ?? null,
  businessName: order.businessName ?? null,
  businessId: order.businessId ?? null,
  branchId: order.branchId ?? null,
  status: order.status ?? null,
  paymentStatus: order.paymentStatus ?? null,
  source: 'account',
  menuSlug: order.menuSlug ?? null,
  total: Number(order.total || 0),
  currency: order.currency ?? null,
  items: Array.isArray(order.items)
   ? order.items.slice(0, 6).map((item) => ({ title: item.title || 'Menu item', quantity: Number(item.quantity || 0) }))
   : [],
  createdAt: order.createdAt ? Date.parse(order.createdAt) || Date.now() : Date.now(),
 };
}

function statusChip(order: NonNullable<ReturnType<typeof useCustomerOrderStatus>['order']>): { label: string; Icon: typeof Clock; tone: 'active' | 'done' | 'cancelled' } {
  if (order.status === 'cancelled') return { label: 'Cancelled', Icon: X, tone: 'cancelled' };
  // An accepted pickup whose payment window lapsed. It showed "Placed" with a
  // live Pay now button (verified live on BiteBox, 2026-10-02).
  if ((order.status as string) === 'expired') return { label: 'Expired', Icon: X, tone: 'cancelled' };
  // "Done" only when fulfilled AND settled — a served order with an open bill
  // is still owed, so it stays on the active rail (the Pay now CTA sits below).
  if (order.status === 'completed' || (order.status === 'served' && order.paymentStatus === 'paid')) return { label: 'Done', Icon: CheckCircle2, tone: 'done' };
  const idx = STEP_INDEX[order.status] ?? 0;
  if (idx >= 2) return { label: 'Ready', Icon: Package, tone: 'active' };
  if (idx === 1) return { label: 'Preparing', Icon: ChefHat, tone: 'active' };
  return { label: 'Placed', Icon: Clock, tone: 'active' };
}

function timeAgo(ms: number): string {
 const diff = Date.now() - ms;
 const mins = Math.round(diff / 60000);
 if (mins < 1) return 'Just now';
 if (mins < 60) return `${mins} min ago`;
 const hours = Math.round(mins / 60);
 if (hours < 24) return `${hours} hr ago`;
 const days = Math.round(hours / 24);
 return `${days} day${days === 1 ? '' : 's'} ago`;
}

/** Live status + pay affordance for one expanded order. Mounts the realtime hook only when open. */
function ExpandedOrder({
 record,
 fallbackSlug,
 onRemoved,
}: {
 record: GuestOrderRecord;
 fallbackSlug: string | null;
 onRemoved: () => void;
}) {
 const { order, loading, error } = useCustomerOrderStatus(record.id, record.token);
 const slug = record.menuSlug || fallbackSlug;
 const fullHref = slug ? legacyOrderTrackPath(slug, record.id, record.token) : null;

 const chip = order ? statusChip(order) : null;
 // Held for the counter's yes: no payment and no ready-time yet (verified live
 // on BiteBox 2026-10-02 — a held order offered "Pay now" before acceptance).
 const awaitingApproval = order ? isAwaitingMerchantApproval(order.pickupApproval) : false;
 const closed = order ? ['expired', 'cancelled'].includes(order.status as string) : false;
 const unpaid = order ? isUnpaidPaymentStatus(order.paymentStatus) && !awaitingApproval && !closed : false;

 return (
 <div className="space-y-2.5 border-t border-[var(--pub-border-subtle)]/50 px-4 py-3">
 {loading ? (
 <ContentSkeleton variant="inline" />
 ) : error || !order ? (
 <p className="text-sm text-[var(--color-text-secondary)]">{error || 'Could not load this order right now.'}</p>
 ) : (
 <>
<div className="flex items-center gap-2" aria-live="polite">
  {chip && (
 <span
 className={cn(
 'inline-flex items-center gap-1.5 rounded-full px-2.5 py-1 text-xs font-semibold',
 chip.tone === 'done'
 ? 'bg-[var(--color-success-soft)] text-[var(--color-success)] '
 : chip.tone === 'cancelled'
 ? 'bg-destructive/15 text-destructive'
 : 'bg-[var(--color-warning-soft)] text-[var(--color-warning)] ',
 )}
 >
 <chip.Icon className={cn('h-3.5 w-3.5', chip.tone === 'active' && 'animate-pulse')} />
 {chip.label}
 </span>
 )}
 {awaitingApproval ? (
 <span className="text-xs text-[var(--color-text-secondary)]">Waiting for the counter to confirm</span>
 ) : !closed && order.eta?.visible && (
 <span className="text-xs text-[var(--color-text-secondary)]">{order.eta.label}</span>
 )}
 </div>
 {/* The code the counter will ask for. The guest saw it once, in the
     dialog they closed; this is where they come back for it. */}
 <PickupClaimCode claim={order.pickupClaim} orderId={record.id} />
 <div className="flex items-center justify-between text-sm">
 <span className="text-[var(--color-text-secondary)]">Total</span>
 <span className="font-semibold">{money(order.total, order.currency || record.currency)}</span>
 </div>
 <div className="grid grid-cols-1 gap-2 pt-1">
 {unpaid && (
 <Button asChild size="sm" className="w-full">
 <Link href={`/pay/${record.id}`}>
 <CreditCard className="mr-1.5 h-4 w-4" /> Pay now
 </Link>
 </Button>
 )}
 {fullHref && (
 <Button asChild size="sm" variant="outline" className="w-full">
 <Link href={fullHref}>
 <ExternalLink className="mr-1.5 h-4 w-4" /> Open full status page
 </Link>
 </Button>
 )}
 <button
 type="button"
 onClick={onRemoved}
 className="mt-0.5 inline-flex items-center justify-center gap-1.5 text-xs text-[var(--color-text-secondary)] transition hover:text-destructive"
 >
 <Trash2 className="h-3.5 w-3.5" /> Remove from this device
 </button>
 </div>
 </>
 )}
 </div>
 );
}

export { useGuestOrderRecords };

/**
 * Every order this person can see, from both places an order can live.
 *
 * There are genuinely two sources and the sheet needs both:
 * 1. **This device** (localStorage) , how a guest with no account finds their
 * way back to an order. Survives a reload, not a new device.
 * 2. **Their account** (`/api/customer-auth/orders`) , the durable history a
 * signed-in customer expects to see on any device they log in from.
 *
 * The sheet previously read source 1 only, so a signed-in customer with a real
 * order history opened "My orders" and was told they had none , the orders were
 * on the server, and nothing ever asked for them.
 *
 * Account records win on collision: both describe the same `service_orders.id`,
 * but the server copy carries the current status and a freshly-minted access
 * token, while the device copy is a snapshot from checkout time.
 */
export function useCustomerOrderRecords(
 signedIn: boolean,
 scope?: { businessId?: string | null; branchId?: string | null },
): {
 records: GuestOrderRecord[];
 loading: boolean;
 loadingMore: boolean;
 hasMore: boolean;
 loadMoreError: string | null;
 loadMore: () => Promise<void>;
} {
 const deviceRecords = useGuestOrderRecords();
 const [accountRecords, setAccountRecords] = useState<GuestOrderRecord[]>([]);
 const [loading, setLoading] = useState(false);
 const [loadingMore, setLoadingMore] = useState(false);
 const [nextCursor, setNextCursor] = useState<string | null>(null);
 const [loadMoreError, setLoadMoreError] = useState<string | null>(null);
 const businessId = scope?.businessId ?? null;
 const branchId = scope?.branchId ?? null;

 useEffect(() => {
 if (!signedIn) {
 setAccountRecords([]);
  setLoading(false);
  setNextCursor(null);
  setLoadMoreError(null);
 return;
 }
 const controller = new AbortController();
 setLoading(true);
 setAccountRecords([]);
 setNextCursor(null);
 setLoadMoreError(null);
 const query = new URLSearchParams({ limit: '50' });
 if (businessId) query.set('businessId', businessId);
 if (branchId) query.set('branchId', branchId);
 void customerFetch(`/api/customer-auth/orders?${query.toString()}`, {
 cache: 'no-store',
 signal: controller.signal,
 })
 .then((res) => (res.ok ? res.json() : null))
 .then((data) => {
 if (controller.signal.aborted || !data?.ok || !Array.isArray(data.orders)) return;
 setAccountRecords(
 data.orders
 .filter((o: { serviceOrderId?: string }) => Boolean(o?.serviceOrderId))
 .map((order: CustomerOrderApiRecord) => projectCustomerOrder(order)),
 );
 setNextCursor(typeof data.nextCursor === 'string' ? data.nextCursor : null);
 })
 .catch(() => {
 // 401 (session expired) or offline , the device list still stands on
 // its own, so this degrades to guest behaviour rather than erroring.
 })
 .finally(() => {
 if (!controller.signal.aborted) setLoading(false);
 });
 return () => controller.abort();
 }, [signedIn, businessId, branchId]);

 const loadMore = useCallback(async () => {
  if (!signedIn || !nextCursor || loadingMore) return;
  setLoadingMore(true);
  setLoadMoreError(null);
  const query = new URLSearchParams({ cursor: nextCursor, limit: '50' });
  if (businessId) query.set('businessId', businessId);
  if (branchId) query.set('branchId', branchId);
  try {
   const response = await customerFetch(`/api/customer-auth/orders?${query.toString()}`, { cache: 'no-store' });
   const data = await response.json() as {
    ok?: boolean;
    orders?: CustomerOrderApiRecord[];
    nextCursor?: string | null;
    error?: string;
   };
   if (!response.ok || !data.ok || !Array.isArray(data.orders)) {
    setLoadMoreError(data.error || 'Could not load more orders right now.');
    return;
   }
   const page = data.orders.filter((order) => Boolean(order?.serviceOrderId)).map(projectCustomerOrder);
   setAccountRecords((previous) => {
    const known = new Set(previous.map((record) => record.id));
    return [...previous, ...page.filter((record) => !known.has(record.id))];
   });
   setNextCursor(typeof data.nextCursor === 'string' ? data.nextCursor : null);
  } catch {
   setLoadMoreError('Could not load more orders right now.');
  } finally {
   setLoadingMore(false);
  }
 }, [signedIn, nextCursor, loadingMore, businessId, branchId]);

 const records = useMemo(() => {
 const byId = new Map<string, GuestOrderRecord>();
 for (const record of deviceRecords) {
  if (businessId && record.businessId !== businessId) continue;
  if (branchId && record.branchId !== branchId) continue;
  byId.set(record.id, record);
 }
 for (const record of accountRecords) byId.set(record.id, record); // account wins
 return Array.from(byId.values()).sort((a, b) => b.createdAt - a.createdAt);
 }, [deviceRecords, accountRecords, businessId, branchId]);

 return { records, loading, loadingMore, hasMore: Boolean(nextCursor), loadMoreError, loadMore };
}

/**
 * A "My orders" sheet that lets a guest re-open any order they placed on this
 * device , no login. Renders nothing until at least one order is stored, so it
 * never adds clutter for a first-time visitor.
 *
 * Two modes: uncontrolled (default) renders its own floating pill, used on the
 * branch page; controlled (`open`/`onOpenChange` supplied) renders only the
 * sheet, so the public menu can drive it from the shared action bar instead of
 * a third FAB fighting for the same corner.
 */
export default function GuestOrdersLauncher({
 palette,
 fallbackSlug = null,
 open: controlledOpen,
 onOpenChange,
 customerSignedIn = false,
 records: providedRecords,
 loading: providedLoading,
 sessionToken = null,
 historyHref = null,
}: {
 palette?: TemplateSurfacePalette;
 fallbackSlug?: string | null;
 open?: boolean;
 onOpenChange?: (open: boolean) => void;
 /**
 * The live table session, when the guest scanned a table QR. Unlocks the
 * "your table" bill above the device history , the two answer different
 * questions ("what did I order?" vs "what does this table owe?") and a guest
 * sharing a table needs the second one.
 */
 sessionToken?: string | null;
 historyHref?: string | null;
 /** Pulls the account's server-side history in alongside this device's list. */
 customerSignedIn?: boolean;
 /**
 * Already-merged records from a parent that calls `useCustomerOrderRecords`
 * itself (the public menu does, to badge its action bar). Supplying them
 * avoids a second identical fetch of the account history on every menu load.
 */
 records?: GuestOrderRecord[];
 loading?: boolean;
}) {
  const own = useCustomerOrderRecords(providedRecords ? false : customerSignedIn);
  const allRecords = providedRecords ?? own.records;
  const loading = providedLoading ?? own.loading;
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  // Read in an effect (not render) so the server-rendered sheet and the
  // hydrated client never disagree about the guest handle from localStorage.
  const [guestUsername, setGuestUsername] = useState<string | null>(null);
  useEffect(() => {
    if (!customerSignedIn) setGuestUsername(getGuestUsername());
  }, [customerSignedIn]);

 /**
 * Orders the table bill above is already itemising. They are dropped from the
 * device list below, because a round the guest just placed otherwise appears
 * twice on the same sheet , once under "Table T1" and again under its own
 * heading, with no indication they are the same order.
 */
 const [sessionOrderIds, setSessionOrderIds] = useState<Set<string>>(new Set());
 const handleSessionOrderIds = useCallback((ids: string[]) => {
 setSessionOrderIds((current) => {
 if (current.size === ids.length && ids.every((id) => current.has(id))) return current;
 return new Set(ids);
 });
 }, []);
 const records = useMemo(
 () => {
 // Only today's orders: the sheet is "where is my order", not a history.
 // It listed every order the device ever placed, back weeks (owner, 2026-10-02).
 const cutoff = Date.now() - 24 * 60 * 60 * 1000;
 return allRecords.filter(
 (record) => record.createdAt >= cutoff && !sessionOrderIds.has(record.id),
 );
 },
 [allRecords, sessionOrderIds],
 );

 const isControlled = controlledOpen !== undefined;
 const open = isControlled ? controlledOpen : uncontrolledOpen;
 const setOpen = useCallback(
 (next: boolean) => {
 if (isControlled) onOpenChange?.(next);
 else setUncontrolledOpen(next);
 },
 [isControlled, onOpenChange],
 );

 // Uncontrolled mode renders its own floating pill, so with nothing to show it
 // must render nothing. Controlled mode is driven by the shared action bar,
 // which now offers "Orders" unconditionally , so the sheet has to be able to
 // open and explain that there's nothing here yet. Returning null in both cases
 // made that button silently do nothing.
 // A live table session is itself something to show, even on a device with no
 // saved orders , the rounds may have been ordered from someone else's phone.
 if (!isControlled && allRecords.length === 0 && !sessionToken) return null;

 const accent = palette?.accent;
 const accentFg = palette?.accentForeground;

 const paletteVars = palette
 ? ({
 '--background': toHslTriplet(palette.background),
 '--foreground': toHslTriplet(palette.foreground),
 '--card': toHslTriplet(palette.card),
 '--card-foreground': toHslTriplet(palette.cardForeground),
 '--muted': toHslTriplet(palette.muted),
 '--muted-foreground': toHslTriplet(palette.mutedForeground),
 '--border': toHslTriplet(palette.border),
 '--primary': toHslTriplet(palette.accent),
 '--primary-foreground': toHslTriplet(palette.accentForeground),
 '--gv-on-accent': palette.accentForeground,
 } as React.CSSProperties)
 : undefined;

 return (
 <>
 {!isControlled ? (
 <button
 type="button"
 onClick={() => setOpen(true)}
 aria-label={`My orders (${allRecords.length})`}
 className="fixed bottom-[max(1rem,env(safe-area-inset-bottom))] left-4 z-40 flex items-center gap-2 rounded-full border border-[var(--pub-border-subtle)]/60 bg-card px-4 py-2.5 text-sm font-semibold shadow-[0_8px_28px_rgba(15,23,32,0.18)] backdrop-blur transition active:scale-95"
 style={
 accent
 ? { backgroundColor: accent, color: accentFg, borderColor: accent }
 : undefined
 }
 >
 <Receipt className="h-4 w-4" />
 My orders
 <span
 className="ml-0.5 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-[var(--color-bg-base)]/25 px-1.5 text-2xs font-bold"
 >
 {allRecords.length}
 </span>
 </button>
 ) : null}

 <Sheet open={open} onOpenChange={setOpen}>
 <SheetContent
 side="bottom"
 className="max-h-[86svh] w-full gap-0 overflow-y-auto rounded-t-3xl border-x-0 border-b-0 p-0 sm:left-1/2 sm:right-auto sm:w-[min(100vw-2rem,28rem)] sm:-translate-x-1/2 sm:rounded-3xl sm:border"
 style={paletteVars ? { ...paletteVars, background: palette?.card, color: palette?.foreground } : undefined}
 overlayStyle={palette ? { backgroundColor: palette.overlay } : undefined}
 >
 <div className="flex shrink-0 justify-center pb-1 pt-2.5">
 <div className="h-1 w-8 rounded-full bg-border/60" />
 </div>
  <SheetHeader className="px-5 pb-2 pt-3 text-left">
  <SheetTitle className="text-base">{sessionToken ? 'Your table & orders' : 'Your recent orders'}</SheetTitle>
  <SheetDescription className="text-xs">
  {sessionToken
  ? 'Everything ordered at this table, then anything else from this device.'
  : customerSignedIn
  ? 'From your account and this device , tap one to see its status or pay.'
  : 'Saved on this device , tap one to see its status or pay. No account needed.'}
  </SheetDescription>
  {!customerSignedIn && guestUsername ? (
  <p className="text-2xs text-[var(--color-text-secondary)]">
  Browsing as @{guestUsername} , sign in to keep your orders everywhere.
  </p>
  ) : null}
  </SheetHeader>

 {historyHref ? (
 <div className="px-4 pb-3">
 <Button asChild variant="outline" className="min-h-11 w-full">
 <Link href={historyHref}>View all orders</Link>
 </Button>
 </div>
 ) : null}

 {sessionToken ? (
 <div className="px-4 pb-1 pt-1">
 <TableSessionBill
 sessionToken={sessionToken}
 open={open}
 onSessionOrderIds={handleSessionOrderIds}
 />
 </div>
 ) : null}

 <div className="space-y-2.5 px-4 pb-6">
 {/* Signed-in history arrives over the network, so say so rather than
 flashing "No orders yet" at someone who has plenty. */}
 {loading && records.length === 0 ? (
 <div className="flex flex-col items-center px-4 py-12 text-center" role="status" aria-live="polite">
 <Loader2 className="size-6 animate-spin text-[var(--color-text-secondary)]" aria-hidden="true" />
 <p className="mt-3 text-sm text-[var(--color-text-secondary)]">Loading your orders…</p>
 </div>
 ) : null}
 {/* Suppressed while a table bill is showing above: "No orders yet" is
 only ever about THIS device's list, and printing it under a
 populated table bill reads as a flat contradiction. */}
 {!loading && records.length === 0 && !sessionToken ? (
 <div className="flex flex-col items-center px-4 py-12 text-center">
 <span className="flex size-14 items-center justify-center rounded-full bg-[var(--color-bg-sunken)] text-[var(--color-text-secondary)]">
 <Receipt className="size-7" aria-hidden="true" />
 </span>
 <p className="mt-4 text-base font-semibold text-[var(--color-text-primary)]">No orders yet</p>
 <p className="mt-1.5 max-w-[26ch] text-xs leading-relaxed text-[var(--color-text-secondary)]">
 {customerSignedIn
 ? 'Nothing on your account or this device yet. Orders you place will show up here.'
 : 'Anything you order here is saved to this device, so you can check its status or pay for it later , without an account.'}
 </p>
 </div>
 ) : null}
 {records.map((record) => {
 const isExpanded = expandedId === record.id;
 return (
 <div key={record.id} className="overflow-hidden rounded-2xl border border-[var(--pub-border-subtle)]/60 bg-card">
 <button
 type="button"
 onClick={() => setExpandedId(isExpanded ? null : record.id)}
 className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left"
 >
 <div className="min-w-0">
 <p className="truncate text-base font-semibold text-[var(--color-text-primary)]">
 Order {record.orderNumber}
 </p>
 <p className="truncate text-xs text-[var(--color-text-secondary)]">
 {record.branchName || record.businessName || 'Your order'} · {timeAgo(record.createdAt)}
 </p>
 </div>
 <div className="flex shrink-0 items-center gap-2">
 <span className="text-sm font-semibold">{money(record.total, record.currency)}</span>
 <ChevronDown
 className={cn('h-4 w-4 text-[var(--color-text-secondary)] transition-transform', isExpanded && 'rotate-180')}
 />
 </div>
 </button>
 {isExpanded && (
 <ExpandedOrder
 record={record}
 fallbackSlug={fallbackSlug}
 onRemoved={() => {
 removeGuestOrder(record.id);
 setExpandedId(null);
 }}
 />
 )}
 </div>
 );
 })}
 </div>
 </SheetContent>
 </Sheet>
 </>
 );
}
